import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomFieldDef } from '../types';
import { createTemplate } from '../api/client';

const LLM_MODELS = [
  'gpt-5.6-sol', 'gpt-5.6-terra', 'gpt-5.6-luna',
  'gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano',
  'gpt-4.1', 'gpt-4.1-mini', 'gpt-4.1-nano', 'gpt-4o', 'gpt-4o-mini',
  'claude-opus-4-8', 'claude-sonnet-5', 'claude-sonnet-4-6', 'claude-haiku-4-5',
];
const STT_OPTIONS = [
  { value: 'deepgram-flux', label: 'Deepgram Flux' },
  { value: 'deepgram-nova-3', label: 'Deepgram Nova-3' },
  { value: 'gladia', label: 'Gladia STT' },
  { value: 'webSpeech', label: 'Web Speech API' },
];
const TTS_OPTIONS = [
  { value: 'openai-tts-1', label: 'OpenAI TTS-1' },
  { value: 'cartesia-sonic-3', label: 'Cartesia Sonic-3' },
  { value: 'elevenlabs-multilingual-v2', label: 'ElevenLabs Multilingual v2' },
  { value: 'browserTts', label: 'Browser TTS' },
];
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'he', label: 'Hebrew' },
  { value: 'ru', label: 'Russian' },
];

export function TemplateCreate() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [greeting, setGreeting] = useState('');
  const [sttProvider, setSttProvider] = useState('deepgram-flux');
  const [ttsProvider, setTtsProvider] = useState('cartesia-sonic-3');
  const [llmModel, setLlmModel] = useState('gpt-5.4-mini');
  const [baseSystemPrompt, setBaseSystemPrompt] = useState(
    'You are a helpful voice assistant. Keep answers concise and natural for speech.',
  );
  const [language, setLanguage] = useState('en');
  const [voiceMode, setVoiceMode] = useState<'pipeline' | 'realtime'>(
    'pipeline',
  );
  const [isPublic, setIsPublic] = useState(true);
  const [customFields, setCustomFields] = useState<CustomFieldDef[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const addField = () => setCustomFields([...customFields, { key: '', label: '', type: 'text', required: false }]);
  const updateField = (i: number, u: Partial<CustomFieldDef>) => setCustomFields(customFields.map((f, j) => j === i ? { ...f, ...u } : f));
  const removeField = (i: number) => setCustomFields(customFields.filter((_, j) => j !== i));

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createTemplate({
        name: name.trim(),
        description: description.trim() || undefined,
        greeting: greeting.trim() || undefined,
        sttProvider, ttsProvider, llmModel, baseSystemPrompt, language,
        voiceMode,
        customFields: customFields.filter((f) => f.key && f.label),
        isPublic,
      });
      navigate('/templates');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.container}>
      <button style={s.backBtn} onClick={() => navigate('/templates')}>&larr; Back</button>
      <h2 style={s.heading}>New Template</h2>

      <div style={s.form}>
        <label style={s.label}>Name * <input style={s.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="My Assistant" /></label>
        <label style={s.label}>Description <input style={s.input} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this agent does" /></label>
        <label style={s.label}>Greeting <input style={s.input} value={greeting} onChange={(e) => setGreeting(e.target.value)} placeholder="Hello! How can I help?" /></label>

        <div style={s.row}>
          <label style={s.label}>Language <select style={s.select} value={language} onChange={(e) => setLanguage(e.target.value)}>{LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}</select></label>
          <label style={s.label}>LLM Model <select style={s.select} value={llmModel} onChange={(e) => setLlmModel(e.target.value)}>{LLM_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}</select></label>
        </div>
        <div style={s.row}>
          <label style={s.label}>STT <select style={s.select} value={sttProvider} onChange={(e) => setSttProvider(e.target.value)}>{STT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
          <label style={s.label}>TTS <select style={s.select} value={ttsProvider} onChange={(e) => setTtsProvider(e.target.value)}>{TTS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
        </div>
        <label style={s.label}>Voice mode <select style={s.select} value={voiceMode} onChange={(e) => setVoiceMode(e.target.value as 'pipeline' | 'realtime')}><option value="pipeline">Pipeline (STT → LLM → TTS, provider metrics)</option><option value="realtime">Realtime (OpenAI speech-to-speech, low latency; STT/TTS ignored)</option></select></label>

        <label style={s.label}>System Prompt <textarea style={s.textarea} value={baseSystemPrompt} onChange={(e) => setBaseSystemPrompt(e.target.value)} rows={4} /></label>
        <label style={{ ...s.label, flexDirection: 'row', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} /> Public (visible in catalog)</label>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>Custom Fields ({customFields.length})</span>
            <button style={s.smallBtn} onClick={addField}>+ Add</button>
          </div>
          {customFields.map((f, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
              <input style={{ ...s.input, flex: 1, padding: '4px 8px', fontSize: 12 }} placeholder="key" value={f.key} onChange={(e) => updateField(i, { key: e.target.value })} />
              <input style={{ ...s.input, flex: 1, padding: '4px 8px', fontSize: 12 }} placeholder="Label" value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} />
              <select style={{ ...s.select, padding: '4px 8px', fontSize: 12 }} value={f.type} onChange={(e) => updateField(i, { type: e.target.value as any })}><option value="text">text</option><option value="textarea">textarea</option><option value="select">select</option></select>
              <label style={{ fontSize: 11 }}><input type="checkbox" checked={f.required} onChange={(e) => updateField(i, { required: e.target.checked })} /> req</label>
              <button style={s.delBtn} onClick={() => removeField(i)}>x</button>
            </div>
          ))}
        </div>

        {error && <div style={s.error}>{error}</div>}
        <button style={s.saveBtn} onClick={handleCreate} disabled={saving || !name.trim()}>{saving ? 'Creating...' : 'Create Template'}</button>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  container: { padding: 20, flex: 1, overflowY: 'auto' },
  backBtn: { background: 'none', border: 'none', color: '#4f46e5', fontSize: 14, cursor: 'pointer', padding: 0, marginBottom: 12 },
  heading: { margin: '0 0 16px', fontSize: 20, color: '#1a1a1a' },
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: { display: 'flex', gap: 10 },
  label: { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, color: '#555', flex: 1 },
  input: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, outline: 'none' },
  select: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, background: '#fff' },
  textarea: { padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', outline: 'none' },
  saveBtn: { padding: '8px 18px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start' },
  smallBtn: { padding: '3px 8px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 11, cursor: 'pointer', color: '#555' },
  delBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 12, cursor: 'pointer' },
  error: { padding: '8px 12px', background: '#fff0f0', border: '1px solid #fcc', borderRadius: 8, color: '#c00', fontSize: 13 },
};
